import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Queue } from '@rossum/api-client/queues';
import { QueueRule } from '@rossum/api-client/schemas';
import { Header } from '@rossum/components/header/Header';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { Button } from '@rossum/ui/material';
import { aiFeaturesEnabledSelector } from '../../../../redux/modules/organizationGroup/selectors';
import { InitialCopilot } from '../../../rules/copilots/InitialCopilot';
import { RulesList } from '../../../rules/rules-list/RulesList';
import { QueueSettingsBreadcrumbs } from '../../components/QueueSettingsBreadcrumbs';
import { QueueSettingsTabs } from '../../components/QueueSettingsTabs';

type RulesPageProps = {
  queue: Queue;
  parentPath: string;
  workspaceName: string;
};

export const RulesPage = ({
  queue,
  parentPath,
  workspaceName,
}: RulesPageProps) => {
  const intl = useIntl();
  const history = useHistory();

  const copilotOpen = history.location.hash === '#new';
  const setCopilotOpen = useCallback(
    (open: boolean) => {
      history.replace({ hash: open ? '#new' : '' });
    },
    [history]
  );

  const aiFeaturesEnabled = useSelector(aiFeaturesEnabledSelector);

  const goToNewRule = (rule: Partial<QueueRule>) =>
    history.push({
      pathname: `${parentPath}/rules/new`,
      state: { rule },
    });

  // Without the copilot, start the rule right away, the same as its Skip button does
  const createRule = () =>
    aiFeaturesEnabled
      ? setCopilotOpen(true)
      : goToNewRule({ queues: [queue.url] });

  return (
    <PageLayout
      renderHeader={params => (
        <Header
          scrollableDivRef={params.scrollableDivRef}
          tabs={<QueueSettingsTabs value="rules" parentPath={parentPath} />}
          breadcrumbs={
            <QueueSettingsBreadcrumbs
              breadcrumbs={[
                {
                  label: intl.formatMessage({
                    id: 'features.queueSettings.rules.list.title',
                  }),
                },
              ]}
              queue={queue}
              workspaceName={workspaceName}
            />
          }
          buttons={[
            <Button
              key="create-rule"
              variant="contained"
              onClick={createRule}
              data-cy="add-rule-button"
              sx={{
                '&:hover': {
                  color: 'inherit',
                },
              }}
            >
              {intl.formatMessage({
                id: 'features.queueSettings.rules.list.addRule',
              })}
            </Button>,
          ]}
          title={intl.formatMessage({
            id: 'features.queueSettings.rules.list.title',
          })}
          description={intl.formatMessage({
            id: 'features.queueSettings.rules.list.description',
          })}
        />
      )}
    >
      <RulesList queue={queue} onCreateRule={createRule} />
      {aiFeaturesEnabled ? (
        <InitialCopilot
          defaultQueue={queue.url}
          allowQueueChange={false}
          onSuccess={value => {
            setCopilotOpen(false);
            goToNewRule(value);
          }}
          onClose={() => setCopilotOpen(false)}
          open={copilotOpen}
        />
      ) : null}
    </PageLayout>
  );
};
